const DEBUG = false;

export function boundingBoxOfLandmarks(landmarks) {
  let minX = null;
  let minY = null;
  let maxX = null;
  let maxY = null;

  landmarks.forEach((landmark) => {
    if (minX === null || landmark.x < minX) {
      minX = landmark.x;
    }
    if (minY === null || landmark.y < minY) {
      minY = landmark.y;
    }
    if (maxX === null || landmark.x > maxX) {
      maxX = landmark.x;
    }
    if (maxY === null || landmark.y > maxY) {
      maxY = landmark.y;
    }
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export function scaleBoxToAspectRatio(box, aspectRatio) {
  // Add padding (10% of width and height)
  const padding = 0.1;

  // Apply padding to the original box
  let x = Math.max(0, box.x - box.width * padding);
  let y = Math.max(0, box.y - box.height * padding);
  let width = Math.min(1 - x, box.width * (1 + padding * 2));
  let height = Math.min(1 - y, box.height * (1 + padding * 2));

  // Calculate center point of the padded box
  const centerX = x + width / 2;
  const centerY = y + height / 2;

  // Calculate current aspect ratio (width / height)
  const currentAspectRatio = width / height;

  let newWidth = width;
  let newHeight = height;

  // Determine which dimension to scale
  if (currentAspectRatio > aspectRatio) {
    // Box is too wide, scale height up
    newHeight = width / aspectRatio;
  } else {
    // Box is too tall, scale width up
    newWidth = height * aspectRatio;
  }

  // Calculate new top-left coordinates while keeping center point
  let newX = centerX - newWidth / 2;
  let newY = centerY - newHeight / 2;

  // Ensure the box stays within image boundaries (0-1) with compensation
  if (newX < 0) {
    // If left edge is out of bounds, try to expand right edge
    const rightExpansion = Math.min(-newX, 1 - (newX + newWidth));
    newWidth += rightExpansion; // Add what we can to the right
    newX = 0;
  }

  if (newY < 0) {
    // If top edge is out of bounds, try to expand bottom edge
    const bottomExpansion = Math.min(-newY, 1 - (newY + newHeight));
    newHeight += bottomExpansion; // Add what we can to the bottom
    newY = 0;
  }

  if (newX + newWidth > 1) {
    // If right edge exceeds boundary, try to expand left edge
    const leftExpansion = Math.min(newX, newX + newWidth - 1);
    newX -= leftExpansion; // Move left if possible
    newWidth = 1 - newX; // Adjust width to fit
  }

  if (newY + newHeight > 1) {
    // If bottom edge exceeds boundary, try to expand top edge
    const topExpansion = Math.min(newY, newY + newHeight - 1);
    newY -= topExpansion; // Move up if possible
    newHeight = 1 - newY; // Adjust height to fit
  }

  // Recalculate aspect ratio after boundary adjustments
  const finalAspectRatio = newWidth / newHeight;

  // Make final aspect ratio adjustment if needed
  if (Math.abs(finalAspectRatio - aspectRatio) > 0.01) {
    if (finalAspectRatio > aspectRatio) {
      // Still too wide, reduce width
      const targetWidth = newHeight * aspectRatio;
      newX += (newWidth - targetWidth) / 2;
      newWidth = targetWidth;
    } else {
      // Still too tall, reduce height
      const targetHeight = newWidth / aspectRatio;
      newY += (newHeight - targetHeight) / 2;
      newHeight = targetHeight;
    }
  }

  return {
    x: newX,
    y: newY,
    width: newWidth,
    height: newHeight,
    centerX,
    centerY,
  };
}

export function drawImageToCanvas(imageElement, landmarks, canvasElement) {
  if (!imageElement || !landmarks || !canvasElement) return;

  const ctx = canvasElement.getContext("2d");
  const boundingBox = boundingBoxOfLandmarks(landmarks);

  const canvasAspectRatio = canvasElement.width / canvasElement.height;

  const scaledBox = scaleBoxToAspectRatio(boundingBox, canvasAspectRatio);

  ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);

  const sourceX = scaledBox.x * imageElement.naturalWidth;
  const sourceY = scaledBox.y * imageElement.naturalHeight;
  const sourceWidth = scaledBox.width * imageElement.naturalWidth;
  const sourceHeight = scaledBox.height * imageElement.naturalHeight;

  ctx.drawImage(
    imageElement,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvasElement.width,
    canvasElement.height
  );
}

export function drawVideoToCanvas(videoElement, landmarks, canvasElement) {
  if (!videoElement || !landmarks || !canvasElement) return;

  const ctx = canvasElement.getContext("2d");
  const boundingBox = boundingBoxOfLandmarks(landmarks);

  const canvasAspectRatio = canvasElement.width / canvasElement.height;

  const scaledBox = scaleBoxToAspectRatio(boundingBox, canvasAspectRatio);

  ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);

  const sourceX = scaledBox.x * videoElement.videoWidth;
  const sourceY = scaledBox.y * videoElement.videoHeight;
  const sourceWidth = scaledBox.width * videoElement.videoWidth;
  const sourceHeight = scaledBox.height * videoElement.videoHeight;

  ctx.drawImage(
    videoElement,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvasElement.width,
    canvasElement.height
  );
}

export function getBlendshapeByName({ blendshapes, name }) {
  return blendshapes.find(
    (blendshape) =>
      blendshape.displayName === name || blendshape.categoryName === name
  );
}

export class FaceComparisonEngine {
  clamp(value, min, max) {
    return Math.max(min, Math.min(value, max));
  }

  computeMouthGaps({ zuckLandmarks, usLandmarks }) {
    if (!zuckLandmarks || !usLandmarks) {
      return { zuckMouthGap: 1, usMouthGap: 0 };
    }
    const zuckBox = boundingBoxOfLandmarks(zuckLandmarks);
    const usBox = boundingBoxOfLandmarks(usLandmarks);
    const zuckDiff = zuckLandmarks[14].y - zuckLandmarks[12].y;
    const usDiff = usLandmarks[14].y - usLandmarks[12].y;
    const zuckBoxHeight = zuckBox.height;
    const usBoxHeight = usBox.height;
    const zuckMouthGap = zuckDiff / zuckBoxHeight;
    const usMouthGap = usDiff / usBoxHeight;

    this.logDebug({ name: "mouthGap", value: zuckMouthGap, who: "ZUCK" });
    this.logDebug({ name: "mouthGap", value: usMouthGap, who: "US" });

    const zuckMouthGapClamped = this.clamp(zuckMouthGap, 0.01, 0.15);
    const usMouthGapClamped = this.clamp(usMouthGap, 0.01, 0.15);

    const absoluteDiff = Math.abs(zuckMouthGapClamped - usMouthGapClamped);
    let relativeDiff = absoluteDiff / zuckMouthGapClamped;
    relativeDiff = relativeDiff ** 3;

    let absoluteScore = 0;
    if (absoluteDiff < 0.03) {
      absoluteScore = 1;
    } else if (absoluteDiff < 0.05) {
      absoluteScore = 0.6;
    } else if (absoluteDiff < 0.07) {
      absoluteScore = 0.1;
    }

    let relativeScore = 0;
    if (relativeDiff < 0.1) {
      relativeScore = 1;
    } else if (relativeDiff < 0.25) {
      relativeScore = 0.6;
    }

    this.logDebug({
      name: "relativeGapScore",
      value: relativeScore,
      who: "US",
    });
    this.logDebug({
      name: "absoluteGapScore",
      value: absoluteScore,
      who: "US",
    });
    let totalScore = 0.7 * absoluteScore + 0.3 * relativeScore;
    this.logDebug({
      name: "totalGapScore",
      value: totalScore,
      who: "US",
    });
    return { gapScore: totalScore, zuckMouthGap };
  }

  compareFeatures({ zuckBlendShapes, usBlendShapes, name, symmetric }) {
    if (symmetric) {
      const targetLeft = this.getBlendshapeValue(
        zuckBlendShapes,
        `${name}Left`,
        "ZUCK"
      );
      const targetRight = this.getBlendshapeValue(
        zuckBlendShapes,
        `${name}Right`,
        "ZUCK"
      );
      const userLeft = this.getBlendshapeValue(
        usBlendShapes,
        `${name}Left`,
        "US"
      );
      const userRight = this.getBlendshapeValue(
        usBlendShapes,
        `${name}Right`,
        "US"
      );
      const targetValue = Math.max(targetLeft, targetRight);
      const userValue = Math.max(userLeft, userRight);
      return this.calculateFeatureScore(targetValue, userValue);
    } else {
      const targetValue = this.getBlendshapeValue(
        zuckBlendShapes,
        name,
        "ZUCK"
      );
      const userValue = this.getBlendshapeValue(usBlendShapes, name, "US");
      return this.calculateFeatureScore(targetValue, userValue);
    }
  }

  calculateFeatureScore(targetValue, userValue) {
    const difference = Math.abs(targetValue - userValue);
    return (1 - difference) ** 2;
  }

  getBlendshapeValue(blendshapes, name, source) {
    const blendshape = blendshapes.find(
      (b) => b.displayName === name || b.categoryName === name
    );

    if (blendshape) {
      this.logDebug({ name, who: source, value: blendshape.score });
    }

    return blendshape ? blendshape.score : null;
  }

  makeDebugElt() {
    const found = document.querySelector("#debug");
    if (found) {
      const zuckElt = found.querySelector("#zuck");
      const usElt = found.querySelector("#us");
      return { zuckElt, usElt };
    }
    const elt = document.createElement("div");
    elt.id = "debug";
    document.body.appendChild(elt);
    elt.style.position = "fixed";
    elt.style.bottom = "10px";
    elt.style.left = "50%";
    elt.style.transform = "translateX(-50%)";
    elt.style.backgroundColor = "white";
    elt.style.padding = "10px";
    elt.style.border = "1px solid black";
    elt.style.zIndex = "1000";
    elt.style.fontFamily = "monospace";
    elt.style.fontSize = "12px";
    elt.style.whiteSpace = "pre";
    elt.style.display = "flex";
    elt.style.flexDirection = "row";
    elt.style.alignItems = "center";
    elt.style.justifyContent = "center";
    elt.style.gap = "16px";

    const zuckElt = document.createElement("div");
    zuckElt.id = "zuck";
    elt.appendChild(zuckElt);
    const usElt = document.createElement("div");
    usElt.id = "us";
    elt.appendChild(usElt);
    return { zuckElt, usElt };
  }

  logDebug({ name, who, value, force = false }) {
    if (!DEBUG && !force) return;
    const { zuckElt, usElt } = this.makeDebugElt();
    let found = document.querySelector(`#${name}-${who}`);
    if (!found) {
      found = document.createElement("div");
      found.id = `${name}-${who}`;
      if (who === "ZUCK") {
        zuckElt.appendChild(found);
      } else {
        usElt.appendChild(found);
      }
    }
    found.textContent = `${name}: ${value.toFixed(2)}`;
  }

  computeScreenshotSuitability({ zuckBlendshapes, zuckMouthGap }) {
    const smilingLeft = this.getBlendshapeValue(
      zuckBlendshapes,
      "mouthSmileLeft",
      "ZUCK"
    );
    const smilingRight = this.getBlendshapeValue(
      zuckBlendshapes,
      "mouthSmileRight",
      "ZUCK"
    );
    const smiling = (smilingLeft + smilingRight) / 2;
    return smiling / (1 - zuckMouthGap) ** 2;
  }

  extractPitchAndYaw({ transformationMatrix }) {
    const ftm = transformationMatrix;
    const [r00, r01, r02, r03] = ftm.slice(0, 4);
    const [r10, r11, r12, r13] = ftm.slice(4, 8);
    const [r20, r21, r22, r23] = ftm.slice(8, 12);
    const [r30, r31, r32, r33] = ftm.slice(12, 16);

    const toDegrees = (x) => x * (180 / Math.PI);
    const pitch = toDegrees(Math.atan2(-r21, Math.sqrt(r20 * r20 + r22 * r22)));
    const yaw = toDegrees(Math.atan2(-r02, r00));
    return { pitch, yaw };
  }
  transformationMatrixSimilarity({
    zuckTransformationMatrixes,
    usTransformationMatrixes,
  }) {
    if (!zuckTransformationMatrixes || !usTransformationMatrixes) {
      return 1;
    }
    let { pitch: zuckPitch, yaw: zuckYaw } = this.extractPitchAndYaw({
      transformationMatrix: zuckTransformationMatrixes.data,
    });
    let { pitch: usPitch, yaw: usYaw } = this.extractPitchAndYaw({
      transformationMatrix: usTransformationMatrixes.data,
    });
    usYaw = usYaw * -1;

    // this.logDebug({
    //   name: "zuckPitch",
    //   who: "ZUCK",
    //   value: zuckPitch,
    //   force: true,
    // });
    // this.logDebug({
    //   name: "usPitch",
    //   who: "US",
    //   value: usPitch,
    //   force: true,
    // });
    // this.logDebug({
    //   name: "zuckYaw",
    //   who: "ZUCK",
    //   value: zuckYaw,
    //   force: true,
    // });
    // this.logDebug({
    //   name: "usYaw",
    //   who: "US",
    //   value: usYaw,
    //   force: true,
    // });
    const pitchDiff = Math.abs(zuckPitch - usPitch);
    const yawDiff = Math.abs(zuckYaw - usYaw);
    let score = 1;
    if (pitchDiff <= 10) {
      const divided = pitchDiff / 10;
      const subtracted = 1 - divided;
      score += subtracted * 0.1;
    }
    if (yawDiff <= 10) {
      const divided = yawDiff / 10;
      const subtracted = 1 - divided;
      score += subtracted * 0.1;
    }
    return score;
  }

  computeScore({
    zuckBlendshapes,
    usBlendshapes,
    zuckLandmarks,
    usLandmarks,
    zuckTransformationMatrixes,
    usTransformationMatrixes,
  }) {
    let { gapScore, zuckMouthGap } = this.computeMouthGaps({
      zuckLandmarks,
      usLandmarks,
    });

    const roundedGapScore = Math.round(gapScore * 100);
    let scoreMult = 0;
    if (roundedGapScore === 100) {
      scoreMult = 1.2;
    } else if (roundedGapScore >= 70) {
      scoreMult = 1;
    } else if (roundedGapScore >= 50) {
      scoreMult = 0.4;
    } else {
      scoreMult = 0.1;
    }

    const features = [
      {
        name: "mouthSmile",
        symmetric: true,
        weight: 0.5,
      },
      { name: "browOuterUp", symmetric: true, weight: 0.2 },
      { name: "mouthPucker", symmetric: false, weight: 0.2 },
      { name: "eyeSquint", symmetric: true, weight: 0.1 },
    ];

    let totalWeight = 0;
    let totalFeatureScore = 0;
    for (const feature of features) {
      const score = this.compareFeatures({
        zuckBlendShapes: zuckBlendshapes,
        usBlendShapes: usBlendshapes,
        name: feature.name,
        symmetric: feature.symmetric,
      });
      totalWeight += feature.weight;
      totalFeatureScore += score * feature.weight;
    }

    const featureScore = totalFeatureScore / totalWeight;
    let score = gapScore * 0.22 + featureScore * 0.78;
    score *= scoreMult;
    const cappedScore = Math.min(score, 1);

    const transformationMatrixScore = this.transformationMatrixSimilarity({
      zuckTransformationMatrixes,
      usTransformationMatrixes,
    });
    score *= transformationMatrixScore;

    const screenshotScore = this.computeScreenshotSuitability({
      zuckBlendshapes,
      zuckMouthGap,
    });

    return {
      score: cappedScore,
      uncappedScore: score,
      screenshotScore,
    };
  }
}
